import { useState, useEffect, useMemo } from 'react';
import { Layout, Menu, Dropdown, message, Modal, Form, Input } from 'antd';
import {
  DashboardOutlined,
  ApiOutlined,
  FileTextOutlined,
  SettingOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  HomeOutlined,
  WalletOutlined,
  MessageOutlined,
  ShopOutlined,
  RobotOutlined,
  UserOutlined,
  LockOutlined,
  SafetyOutlined,
  MailOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  EditOutlined,
  ThunderboltOutlined,
  FireOutlined,
  SoundOutlined,
  BarChartOutlined,
} from '@ant-design/icons';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { userAPI } from '../services/api';
import { BASE_URL } from '../utils/apiUrl';
import { t, getLang, setLang } from '../utils/i18n';
import CustomerService from '../components/CustomerService';

const { Header, Sider } = Layout;

const UserLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [lang, setLangState] = useState(getLang());
  const [accountModalVisible, setAccountModalVisible] = useState(false);
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordForm] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    fetchUserInfo();
  }, []);

  const fetchUserInfo = async () => {
    try {
      const response = await userAPI.getInfo();
      if (response.code === 0 && response.data) {
        setUserInfo(response.data);
        localStorage.setItem('user_email', response.data.email || '');
        if (response.data.created_at) {
          localStorage.setItem('user_created_at', response.data.created_at);
        }
      }
    } catch (error) {
      // ignore
    }
  };

  // 语言切换
  const handleLangChange = (newLang) => {
    setLang(newLang);
    setLangState(newLang);
    window.location.reload();
  };

  const menuItems = useMemo(() => {
    const menus = [
      {
        key: '/dashboard',
        icon: <DashboardOutlined />,
        label: t('dashboard'),
      },
      {
        key: '/dashboard/models',
        icon: <RobotOutlined />,
        label: t('model_square'),
      },
      {
        key: '/dashboard/apikeys',
        icon: <ApiOutlined />,
        label: t('api_keys'),
      },
      {
        key: '/dashboard/bills',
        icon: <FileTextOutlined />,
        label: t('bills'),
      },
      {
        key: '/dashboard/usage',
        icon: <BarChartOutlined />,
        label: t('usage_analysis'),
      },
      {
        key: '/dashboard/presets',
        icon: <RobotOutlined />,
        label: t('preset_management'),
      },
      {
        key: '/dashboard/model-training',
        icon: <ThunderboltOutlined />,
        label: t('playground'),
      },
      {
        key: '/dashboard/batches',
        icon: <ThunderboltOutlined />,
        label: t('batch_processing') || '批量处理',
      },
      {
        key: '/dashboard/creative',
        icon: <FireOutlined style={{ color: '#f5222d' }} />,
        label: (
          <span>
            {t('creative_inspiration')}
            <span style={{
              background: '#ff4d4f',
              color: '#fff',
              fontSize: 10,
              padding: '1px 5px',
              borderRadius: 4,
              marginLeft: 6,
              fontWeight: 'bold',
              position: 'relative',
              top: -1,
            }}>HOT</span>
          </span>
        ),
      },
      {
        key: '/dashboard/audio',
        icon: <SoundOutlined />,
        label: t('audio_studio'),
      },
      {
        key: '/dashboard/recharge',
        icon: <WalletOutlined />,
        label: t('top_up'),
      },
      {
        key: '/dashboard/feedback',
        icon: <MessageOutlined />,
        label: t('feedback'),
      },
    ];
    if (userInfo?.is_merchant && userInfo?.affiliate_active) {
      menus.push({
        key: '/dashboard/merchant',
        icon: <ShopOutlined />,
        label: t('merchant_center'),
      });
    }
    return menus;
  }, [lang, userInfo?.is_merchant, userInfo?.affiliate_active]);

  const handleMenuClick = (e) => {
    navigate(e.key);
  };

  const getSelectedKey = () => {
    const path = location.pathname;
    if (path === '/dashboard') return '/dashboard';
    return path;
  };

  const handleLogout = () => {
    localStorage.removeItem('user_token');
    message.success(t('logout'));
    window.location.href = '/login';
  };

  // 修改密码
  const handlePasswordChange = async (values) => {
    if (values.newPassword !== values.confirmPassword) {
      message.error('两次输入的密码不一致');
      return;
    }
    setPasswordLoading(true);
    try {
      const token = localStorage.getItem('user_token');
      const response = await fetch(`${BASE_URL}/api/user/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          old_password: values.currentPassword,
          new_password: values.newPassword,
        }),
      });
      const data = await response.json();
      if (data.code === 0) {
        message.success('密码修改成功');
        setPasswordModalVisible(false);
        passwordForm.resetFields();
      } else {
        message.error(data.message || '修改失败');
      }
    } catch (error) {
      message.error('修改失败，请稍后重试');
    } finally {
      setPasswordLoading(false);
    }
  };

  const userMenuItems = [
    {
      key: 'account',
      icon: <UserOutlined />,
      label: t('account_settings'),
    },
    {
      key: 'password',
      icon: <LockOutlined />,
      label: t('change_password'),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('logout'),
      danger: true,
    },
  ];

  const handleUserMenuClick = ({ key }) => {
    if (key === 'logout') {
      handleLogout();
    } else if (key === 'account') {
      setAccountModalVisible(true);
    } else if (key === 'password') {
      setPasswordModalVisible(true);
    }
  };

  const email = localStorage.getItem('user_email') || '-';
  const createdAt = localStorage.getItem('user_created_at') || '-';

  return (
    <Layout className="user-layout" hasSider>
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        trigger={null}
        width={240}
        collapsedWidth={72}
        className="user-sider"
      >
        <Link to="/" className="sidebar-logo">
          <img src={require('../utils/img/logo.png')} alt="Blue API" className="logo-icon" />
          {!collapsed && <span className="logo-text">Blue API</span>}
        </Link>

        <div className="sidebar-menu-wrapper">
          <Menu
            className="sidebar-menu"
            mode="inline"
            selectedKeys={[getSelectedKey()]}
            items={menuItems}
            onClick={handleMenuClick}
          />
        </div>
      </Sider>

      <div className={`user-content-wrapper ${collapsed ? 'user-sider-collapsed' : ''}`}>
        <Header className="user-header">
          <div className="header-left">
            <span
              onClick={() => setCollapsed(!collapsed)}
              style={{
                cursor: 'pointer',
                fontSize: 16,
                color: '#6b7280',
                width: 32,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 6,
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            </span>
            <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#6b7280', marginLeft: 8 }}>
              <HomeOutlined />
              <span style={{ fontSize: 13 }}>{t('back_to_home')}</span>
            </Link>
          </div>

          <div className="header-right">
            <div className="user-lang-switch">
              <div className={`lang-slider ${lang === 'en' ? 'en' : ''}`}></div>
              <span className={lang === 'zh' ? 'active' : ''} onClick={() => handleLangChange('zh')}>中</span>
              <span className={lang === 'en' ? 'active' : ''} onClick={() => handleLangChange('en')}>EN</span>
            </div>
            <Dropdown
              menu={{ items: userMenuItems, onClick: handleUserMenuClick }}
              placement="bottomRight"
              trigger={['click']}
            >
              <div className="user-avatar-wrapper">
                <img src={require('../utils/img/mrtx.png')} alt="Avatar" className="user-avatar-img" />
                {!collapsed && (
                  <span style={{ fontSize: 14, color: '#374151', fontWeight: 500 }}>
                    {userInfo?.email || t('user')}
                  </span>
                )}
              </div>
            </Dropdown>
          </div>
        </Header>

        <div className="user-content">
          <Outlet />
        </div>

        <CustomerService />
      </div>

      {/* 账户设置弹窗 */}
      <Modal
        title={null}
        open={accountModalVisible}
        onCancel={() => setAccountModalVisible(false)}
        footer={null}
        width={400}
        centered
        className="account-modal"
      >
        <div className="account-modal-content">
          <div className="modal-section">
            <div className="modal-section-title">
              <UserOutlined />
              <span>{t('basic_info')}</span>
            </div>
            <div className="modal-info-list">
              <div className="modal-info-row">
                <div className="modal-info-label">
                  <MailOutlined />
                  <span>{t('email')}</span>
                </div>
                <div className="modal-info-value">{email}</div>
              </div>
              <div className="modal-info-row">
                <div className="modal-info-label">
                  <ClockCircleOutlined />
                  <span>{t('registered_at')}</span>
                </div>
                <div className="modal-info-value">{createdAt}</div>
              </div>
              <div className="modal-info-row">
                <div className="modal-info-label">
                  <CheckCircleOutlined />
                  <span>{t('status')}</span>
                </div>
                <div className="modal-info-value">
                  <span className="modal-status-tag">{t('normal')}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-section">
            <div className="modal-section-title">
              <SafetyOutlined />
              <span>{t('security_settings')}</span>
            </div>
            <div className="modal-info-list">
              <div 
                className="modal-info-row clickable" 
                onClick={() => {
                  setAccountModalVisible(false);
                  setPasswordModalVisible(true);
                }}
              >
                <div className="modal-info-label">
                  <LockOutlined />
                  <span>{t('login_password')}</span>
                </div>
                <div className="modal-info-value action">
                  <span className="link-text">{t('edit')}</span>
                  <EditOutlined className="edit-icon" />
                </div>
              </div>
              <div className="modal-info-row">
                <div className="modal-info-label">
                  <MailOutlined />
                  <span>{t('email')}</span>
                </div>
                <div className="modal-info-value">
                  <span className="modal-verified-tag">
                    <CheckCircleOutlined /> {t('active')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* 修改密码弹窗 */}
      <Modal
        title={t('change_password')}
        open={passwordModalVisible}
        onCancel={() => {
          setPasswordModalVisible(false);
          passwordForm.resetFields();
        }}
        footer={null}
        width={380}
        centered
      >
        <Form
          form={passwordForm}
          onFinish={handlePasswordChange}
          layout="vertical"
          style={{ marginTop: 20 }}
        >
          <Form.Item
            name="currentPassword"
            label={t('old_password')}
            rules={[{ required: true, message: t('enter_current_password') }]}
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: '#bfbfbf' }} />}
              placeholder={t('enter_current_password')}
            />
          </Form.Item>

          <Form.Item
            name="newPassword"
            label={t('new_password')}
            rules={[
              { required: true, message: t('enter_new_password') },
              { min: 8, message: t('password_min_length') },
            ]}
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: '#bfbfbf' }} />}
              placeholder={t('password_hint_input')}
            />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label={t('confirm_password')}
            dependencies={['newPassword']}
            rules={[
              { required: true, message: t('confirm_new_password') },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(t('password_mismatch')));
                },
              }),
            ]}
          >
            <Input.Password
              prefix={<CheckCircleOutlined style={{ color: '#bfbfbf' }} />}
              placeholder={t('confirm_new_password')}
            />
          </Form.Item>

          <div className="modal-form-footer">
            <button
              type="button"
              className="btn-cancel"
              onClick={() => {
                setPasswordModalVisible(false);
                passwordForm.resetFields();
              }}
            >
              {t('cancel')}
            </button>
            <button
              type="submit"
              className="btn-submit"
              disabled={passwordLoading}
              onClick={() => passwordForm.submit()}
            >
              {passwordLoading ? t('saving') : t('confirm')}
            </button>
          </div>
        </Form>
      </Modal>

      <style>{`
        .account-modal .ant-modal-content {
          padding: 0;
          border-radius: 12px;
          overflow: hidden;
        }

        .account-modal .ant-modal-body {
          padding: 0;
        }

        .account-modal-content {
          padding: 20px 24px;
        }

        .modal-section {
          margin-bottom: 20px;
        }

        .modal-section:last-child {
          margin-bottom: 0;
        }

        .modal-section-title {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 12px;
          padding-bottom: 10px;
          border-bottom: 1px solid #f0f0f0;
        }

        .modal-section-title .anticon {
          color: #6b7280;
        }

        .modal-info-list {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .modal-info-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 10px 0;
          border-bottom: 1px solid #fafafa;
        }

        .modal-info-row:last-child {
          border-bottom: none;
        }

        .modal-info-row.clickable {
          cursor: pointer;
          border-radius: 6px;
          margin: 0 -8px;
          padding: 10px 8px;
          transition: background 0.2s;
        }

        .modal-info-row.clickable:hover {
          background: #f9fafb;
        }

        .modal-info-label {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #6b7280;
          font-size: 13px;
        }

        .modal-info-label .anticon {
          font-size: 13px;
          color: #9ca3af;
        }

        .modal-info-value {
          font-size: 13px;
          color: #374151;
        }

        .modal-info-value.action {
          display: flex;
          align-items: center;
          gap: 4px;
          color: #1677ff;
        }

        .modal-status-tag {
          display: inline-block;
          padding: 2px 8px;
          background: #f6ffed;
          color: #52c41a;
          border-radius: 4px;
          font-size: 12px;
        }

        .modal-verified-tag {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          color: #52c41a;
          font-size: 12px;
        }

        .modal-form-footer {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          margin-top: 24px;
          padding-top: 16px;
          border-top: 1px solid #f0f0f0;
        }

        .btn-cancel {
          padding: 8px 20px;
          background: #fff;
          color: #666;
          border: 1px solid #d9d9d9;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
        }

        .btn-cancel:hover {
          border-color: #1677ff;
          color: #1677ff;
        }

        .btn-submit {
          padding: 8px 20px;
          background: #1677ff;
          color: #fff;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
        }

        .btn-submit:hover {
          background: #4096ff;
        }

        .btn-submit:disabled {
          opacity: 0.65;
          cursor: not-allowed;
        }

        .ant-input-affix-wrapper {
          border-radius: 6px;
        }
      `}</style>
    </Layout>
  );
};

export default UserLayout;
